import { useEffect, useMemo, useState } from 'react';
import {
  AppBar,
  Avatar,
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Button,
  Container,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Toolbar,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import FolderOpenRoundedIcon from '@mui/icons-material/FolderOpenRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import HubRoundedIcon from '@mui/icons-material/HubRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import { useSearchParams } from 'react-router-dom';

import { useAuth } from '../../../context/AuthContext';
import ProjectVitalAddDialog from '../components/ProjectVitalAddDialog';
import ProjectVitalHomeView from '../views/ProjectVitalHomeView';
import ProjectVitalIssuesView from '../views/ProjectVitalIssuesView';
import ProjectVitalFinancesView from '../views/ProjectVitalFinancesView';
import ProjectVitalActivityView from '../views/ProjectVitalActivityView';
import ProjectVitalDataView from '../views/ProjectVitalDataView';

type ProjectVitalViewKey =
  | 'inicio'
  | 'asuntos'
  | 'finanzas'
  | 'actividad'
  | 'datos';

const desktopNavWidth = 228;

const sections: Array<{
  key: ProjectVitalViewKey;
  label: string;
  icon: React.ReactNode;
}> = [
  {
    key: 'inicio',
    label: 'Inicio',
    icon: <HomeRoundedIcon />,
  },
  {
    key: 'asuntos',
    label: 'Asuntos',
    icon: <FolderOpenRoundedIcon />,
  },
  {
    key: 'finanzas',
    label: 'Finanzas',
    icon: <AccountBalanceWalletRoundedIcon />,
  },
  {
    key: 'actividad',
    label: 'Actividad',
    icon: <HistoryRoundedIcon />,
  },
  {
    key: 'datos',
    label: 'Datos',
    icon: <HubRoundedIcon />,
  },
];

const VALID_VIEWS = new Set<ProjectVitalViewKey>(
  sections.map((section) => section.key),
);

function isProjectVitalViewKey(value: string | null): value is ProjectVitalViewKey {
  return Boolean(value && VALID_VIEWS.has(value as ProjectVitalViewKey));
}

export default function ProjectVitalPage() {
  const { user, signOut } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [addOpen, setAddOpen] = useState(false);

  const requestedView = searchParams.get('view');

  const view: ProjectVitalViewKey = isProjectVitalViewKey(requestedView)
    ? requestedView
    : 'inicio';

  useEffect(() => {
    if (!isProjectVitalViewKey(requestedView)) {
      setSearchParams({ view: 'inicio' }, { replace: true });
    }
  }, [requestedView, setSearchParams]);

  const currentIndex = Math.max(
    0,
    sections.findIndex((section) => section.key === view),
  );

  const content = useMemo(() => {
    switch (view) {
      case 'asuntos':
        return <ProjectVitalIssuesView />;
      case 'finanzas':
        return <ProjectVitalFinancesView />;
      case 'actividad':
        return <ProjectVitalActivityView />;
      case 'datos':
        return <ProjectVitalDataView />;
      case 'inicio':
      default:
        return <ProjectVitalHomeView />;
    }
  }, [view]);

  const setCurrentView = (nextView: ProjectVitalViewKey) => {
    setSearchParams({ view: nextView }, { replace: true });
  };

  const handleLogout = async () => {
    await signOut();
    window.location.href = '/project-vital/login';
  };

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        bgcolor: '#0A192F',
        color: 'text.primary',
      }}
    >
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: alpha('#0f172a', 0.94),
          backdropFilter: 'blur(14px)',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Container maxWidth={false} sx={{ px: { xs: 2, sm: 3 } }}>
          <Toolbar disableGutters sx={{ minHeight: { xs: 60, md: 64 } }}>
            <Box
              component="button"
              type="button"
              onClick={() => setCurrentView('inicio')}
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 0,
                border: 0,
                bgcolor: 'transparent',
                color: 'inherit',
                cursor: 'pointer',
                textAlign: 'left',
                font: 'inherit',
              }}
            >
              <Box
                component="img"
                src="/logo.png"
                alt="PROA"
                sx={{
                  height: { xs: 27, md: 30 },
                  width: 'auto',
                  mr: 1.25,
                  filter: 'drop-shadow(0 0 8px rgba(77,208,225,0.22))',
                }}
              />

              <Box>
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: '0.98rem', sm: '1.05rem' },
                    lineHeight: 1.1,
                    color: '#f8fafc',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Proyecto Vital
                </Typography>

                <Typography
                  sx={{
                    display: { xs: 'none', sm: 'block' },
                    mt: 0.2,
                    fontSize: '0.67rem',
                    fontWeight: 700,
                    color: '#64748b',
                    letterSpacing: 0.9,
                    textTransform: 'uppercase',
                  }}
                >
                  Personal workspace
                </Typography>
              </Box>
            </Box>

            <Box sx={{ flexGrow: 1 }} />

            <Button
              variant="contained"
              startIcon={<AddRoundedIcon />}
              onClick={() => setAddOpen(true)}
              sx={{
                mr: { xs: 0.75, sm: 1.5 },
                px: { xs: 1.4, sm: 2 },
                minWidth: 0,
                fontWeight: 800,
                bgcolor: '#4DD0E1',
                color: '#0A192F',
                '&:hover': {
                  bgcolor: '#88FFFF',
                },
              }}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                Añadir
              </Box>
            </Button>

            <Tooltip title={user?.email || 'Usuario'}>
              <Avatar
                sx={{
                  width: 34,
                  height: 34,
                  bgcolor: alpha('#4DD0E1', 0.12),
                  color: '#4DD0E1',
                  border: '1px solid rgba(77,208,225,0.22)',
                }}
              >
                <PersonRoundedIcon fontSize="small" />
              </Avatar>
            </Tooltip>

            <Tooltip title="Cerrar sesión">
              <IconButton
                onClick={() => void handleLogout()}
                sx={{
                  ml: 0.5,
                  color: '#94a3b8',
                  '&:hover': {
                    color: '#f8fafc',
                    bgcolor: alpha('#fff', 0.05),
                  },
                }}
              >
                <LogoutRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Toolbar>
        </Container>
      </AppBar>

      <Paper
        component="aside"
        square
        elevation={0}
        sx={{
          display: { xs: 'none', md: 'block' },
          position: 'fixed',
          top: 64,
          bottom: 0,
          left: 0,
          width: desktopNavWidth,
          bgcolor: '#0f172a',
          borderRight: '1px solid rgba(255,255,255,0.08)',
          zIndex: 1100,
        }}
      >
        <List
          component="nav"
          aria-label="Vistas de Proyecto Vital"
          sx={{ px: 1.5, py: 2 }}
        >
          {sections.map((section) => {
            const selected = section.key === view;

            return (
              <ListItemButton
                key={section.key}
                selected={selected}
                onClick={() => setCurrentView(section.key)}
                sx={{
                  mb: 0.5,
                  minHeight: 46,
                  borderRadius: 2,
                  color: selected ? '#f8fafc' : '#94a3b8',
                  '&.Mui-selected': {
                    bgcolor: alpha('#4DD0E1', 0.10),
                    color: '#4DD0E1',
                  },
                  '&.Mui-selected:hover': {
                    bgcolor: alpha('#4DD0E1', 0.14),
                  },
                  '&:hover': {
                    bgcolor: alpha('#fff', 0.04),
                    color: '#f8fafc',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 38,
                    color: 'inherit',
                  }}
                >
                  {section.icon}
                </ListItemIcon>

                <ListItemText
                  primary={
                    section.key === 'datos'
                      ? 'Datos e integraciones'
                      : section.label
                  }
                  primaryTypographyProps={{
                    fontSize: '0.9rem',
                    fontWeight: selected ? 700 : 500,
                  }}
                />
              </ListItemButton>
            );
          })}
        </List>
      </Paper>

      <Box
        component="main"
        sx={{
          pt: { xs: '60px', md: '64px' },
          ml: { xs: 0, md: `${desktopNavWidth}px` },
          pb: {
            xs: 'calc(82px + env(safe-area-inset-bottom))',
            md: 4,
          },
          minHeight: '100dvh',
        }}
      >
        <Container
          maxWidth={false}
          sx={{
            px: { xs: 2, sm: 3, lg: 4, xl: 5 },
            py: { xs: 3, md: 4 },
            width: '100%',
            maxWidth: 'none',
            ml: 0,
          }}
        >
          {content}
        </Container>
      </Box>

      <Paper
        elevation={12}
        sx={{
          display: { xs: 'block', md: 'none' },
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1200,
          bgcolor: alpha('#0f172a', 0.97),
          borderTop: '1px solid rgba(255,255,255,0.08)',
          pb: 'env(safe-area-inset-bottom)',
          backdropFilter: 'blur(14px)',
        }}
      >
        <BottomNavigation
          showLabels
          value={currentIndex}
          onChange={(_, nextIndex: number) => {
            const nextSection = sections[nextIndex];

            if (nextSection) {
              setCurrentView(nextSection.key);
            }
          }}
          sx={{
            height: 64,
            bgcolor: 'transparent',
            '& .MuiBottomNavigationAction-root': {
              minWidth: 0,
              px: 0.4,
              color: '#64748b',
            },
            '& .Mui-selected': {
              color: '#4DD0E1',
            },
            '& .MuiBottomNavigationAction-label': {
              fontSize: '0.66rem',
              mt: 0.2,
            },
            '& .Mui-selected .MuiBottomNavigationAction-label': {
              fontSize: '0.66rem',
              fontWeight: 700,
            },
          }}
        >
          {sections.map((section) => (
            <BottomNavigationAction
              key={section.key}
              label={section.label}
              icon={section.icon}
            />
          ))}
        </BottomNavigation>
      </Paper>

      <ProjectVitalAddDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
      />
    </Box>
  );
}
